import { OrderEdit } from '@medusajs/medusa'
import { Order } from '@miles/domain/Store/Models/Order/Order'
import {
  AttendeeProfile,
  DecisionReason,
  DecisionReasonType,
  ParticipantsExportFormat
} from '@prisma/client'
import { AxiosError } from 'axios'
import { FieldValues } from 'react-hook-form'
import { Fetcher } from 'swr'
import { z } from 'zod'
import { DiscountAlreadyUsedStatus } from 'components/Dashboard/Discounts/discountAlreadyUsed'
import { AttendeeBasicInformation } from 'components/Dashboard/Hooks/useAttendeesById'
import { FormattedManualRegistrationRaceInfo } from 'components/Dashboard/ManualRegistrationOrEditModal'
import { FormWithRaces } from 'components/Options/CustomizedForm/FormTypes'
import { BulkDeleteDiscountsResult } from 'domain/application/services/store/AdminDiscountService'
import { Filter } from 'domain/models/AttendeeSortField/AttendeeSortField'
import { GroupList } from 'domain/models/Group/EntityGroupList'
import {
  FormProductWithRaces,
  ImportRace
} from 'domain/models/Import/ImportAttendee'
import {
  AttendeeId,
  EditionId,
  GroupId,
  OrderId,
  RaceId,
  ReductionId
} from 'domain/models/ValueObject/EntityIds'
import { GetWithFiltersResponse } from 'domain/models/WaitingLists/WaitingListsRace'
import { ExportRequest } from 'pages/api/attendees/export'
import { apiClient } from 'services/api/client'
import {
  AttendeeList,
  GetAttendeeFromId,
  GetAttendeeInfo,
  GetAttendeesFromDraftOrder,
  GetAttendeesFromOrder
} from 'services/api/database/AttendeeService'
import { GetEventAndEditionFromEditionId } from 'services/api/database/EditionService'
import { GetRacesFromEditionIdType } from 'services/api/database/RaceService'
import { UploadedDocumentWithLink } from 'services/api/database/UploadedDocumentService'
import { ArchiveFormSchemaType } from 'services/validation/form/form'
import { UploadType } from 'services/validation/get-presigned-url'
import { storefrontApiClient } from 'store/clients/storefrontApiClient'

export type ExportError = 'NO_DATA_FOUND' | 'UNKNOWN_ERROR'

export type EditionExportError = ExportError | 'EDITION_NOT_FOUND'

export type RaceExportError = ExportError | 'RACE_NOT_FOUND'

type getAttendeesArgs = {
  attendeeIds?: string[]
  displayCancelled: boolean
  editionId?: EditionId
  raceIds?: RaceId[]
  searchQuery?: string
  page?: number
  pageSize?: number
  sortField: string | undefined
  sortDirection: 'asc' | 'desc' | undefined | null
  start?: number
  end?: number
  filter?: Filter
  isCaptainOnly?: boolean
}

type getAwaitingRegistrationsArgs = {
  editionId: EditionId
  raceId: RaceId
  sortField: string | undefined
  sortDirection: 'asc' | 'desc' | undefined | null
  start: number
  end: number
  filter?: Filter
  searchQuery?: string
  displayRegistered?: boolean
}

export type getRacesArgs = {
  editionId: EditionId | undefined
}
type getOptionsArgs = {
  editionId: EditionId
  currency: string
}

export type GetGroupArgs = {
  editionId: EditionId | undefined
}

interface UpdatePartnerOptionArgs extends FieldValues {
  partnerOptionId: string
  editionId: number
}

export const SWR_GET_CERTIFICATE_KEY = '/justification-documents?editionId='

export class DashboardApiClient {
  private buildGetAttendeesBody({
    editionId,
    raceIds,
    searchQuery
  }: Pick<getAttendeesArgs, 'editionId' | 'raceIds' | 'searchQuery'>) {
    if (searchQuery && raceIds && raceIds?.length > 0) {
      return { searchQuery, raceIds }
    } else if (searchQuery && editionId) {
      return { searchQuery, editionId }
    } else if (raceIds && raceIds.length > 0) {
      return { raceIds }
    } else {
      return { editionId }
    }
  }

  async getAwaitingRegistrations({
    editionId,
    raceId,
    end,
    filter,
    searchQuery,
    sortDirection,
    sortField,
    start,
    displayRegistered
  }: getAwaitingRegistrationsArgs) {
    const { data } = await apiClient.post<GetWithFiltersResponse>(
      'waiting-lists/get-attendees',
      {
        editionId,
        raceId,
        end,
        filter,
        searchQuery,
        sortDirection,
        sortField,
        start,
        displayRegistered
      }
    )
    return {
      ...data,
      attendees: data.attendees.map((attendee) => ({
        ...attendee,
        createdAt: new Date(attendee.createdAt),
        updatedAt: attendee.updatedAt
          ? new Date(attendee.updatedAt)
          : undefined,
        birthDate: attendee.birthDate ? new Date(attendee.birthDate) : undefined
      }))
    }
  }

  async getAttendeesIds({
    attendeeIds,
    editionId,
    raceIds,
    searchQuery,
    sortDirection,
    sortField,
    start,
    end,
    filter,
    isCaptainOnly
  }: Omit<getAttendeesArgs, 'displayCancelled'>) {
    const { data } = await apiClient.post<{
      attendees: AttendeeBasicInformation[]
    }>('/attendees/get-ids-with-filters', {
      ...this.buildGetAttendeesBody({
        editionId,
        raceIds,
        searchQuery
      }),
      attendeeIds,
      sortDirection,
      sortField,
      start,
      end,
      filter,
      isCaptainOnly
    })
    return data.attendees
  }

  async getAttendees({
    editionId,
    raceIds,
    searchQuery,
    sortDirection,
    sortField,
    start,
    end,
    filter,
    displayCancelled
  }: getAttendeesArgs) {
    const { data } = await apiClient.post<{ attendees: AttendeeList }>(
      '/attendees/get-with-lazy-loading',
      {
        ...this.buildGetAttendeesBody({
          editionId,
          raceIds,
          searchQuery
        }),
        displayCancelled,
        sortDirection,
        sortField,
        start,
        end,
        filter
      }
    )
    return data.attendees
  }

  getAttendee({ attendeeId }: { attendeeId: string | null }) {
    return {
      fetcher: () =>
        apiClient.get<GetAttendeeFromId>('/attendees/get', {
          params: { attendeeId }
        }),
      // the request will not be sent for as long as the key is null
      // as per the doc https://swr.vercel.app/docs/conditional-fetching#conditional
      key: attendeeId ? `/attendees/get?id=${attendeeId}` : null
    }
  }
  getAttendeeInfo(attendeeId: AttendeeId | null) {
    return {
      fetcher: () =>
        apiClient.get<GetAttendeeInfo>('/attendees/getInfo', {
          params: { attendeeId }
        }),
      // the request will not be sent for as long as the key is null
      // as per the doc https://swr.vercel.app/docs/conditional-fetching#conditional
      key: attendeeId ? `/attendees/getInfo?id=${attendeeId}` : null
    }
  }

  getAttendeeProfile() {
    return {
      fetcher: () =>
        apiClient.get<AttendeeProfile | null>('/attendeesProfile/get'),
      key: '/attendeesProfile/get'
    }
  }

  getOptionsFromRaceSlug({ raceSlug }: { raceSlug?: string }) {
    return {
      fetcher: () =>
        apiClient.get(`/races/${raceSlug}/options`).then((res) => res.data),
      // the request will not be sent for as long as the key is null
      // as per the doc https://swr.vercel.app/docs/conditional-fetching#conditional
      key: raceSlug ? `/races/${raceSlug}/options` : null
    }
  }

  getRaceInfoForManualRegistration(raceId: RaceId | undefined) {
    return {
      fetcher: () =>
        apiClient.get<FormattedManualRegistrationRaceInfo | undefined>(
          '/races/get-race-info-for-manual-registration',
          {
            params: { raceId }
          }
        ),
      key: raceId
        ? `/races/get-race-info-for-manual-registration?raceId=${raceId}`
        : null
    }
  }

  getUnarchivedRaces({ editionId }: getRacesArgs) {
    return {
      fetcher: () =>
        apiClient.get<GetRacesFromEditionIdType[]>(
          '/races/get-unarchived-races',
          {
            params: { editionId }
          }
        ),
      key: editionId ? `/races/get-unarchived-races/${editionId}` : null
    }
  }

  getImportRaces({ editionId }: getRacesArgs) {
    return {
      fetcher: () =>
        apiClient.get<ImportRace[]>('/import/get-import-races', {
          params: { editionId }
        }),
      key: editionId ? `/import/get-import-races/${editionId}` : null
    }
  }

  getImportOptions({ editionId, currency }: getOptionsArgs) {
    return {
      fetcher: async () => {
        const options = await apiClient.get<FormWithRaces[]>('/options', {
          params: { editionId }
        })

        const optionsWithProducts: FormProductWithRaces[] = await Promise.all(
          options.data
            .filter((option) => !option.isArchived && option.isActive)
            .map(async (option) => {
              const formVariableFields = option.form.variableFields as {
                productId: string
              }
              const product =
                (
                  await storefrontApiClient.getProductsAndInventoriesByIds(
                    [formVariableFields.productId],
                    currency
                  )
                )[0] ?? null
              return {
                ...option,
                product
              }
            })
        )

        return optionsWithProducts
      },
      key: editionId ? `/options/${editionId}` : null
    }
  }

  getEditionAndEventFromEditionId({ editionId }: getRacesArgs) {
    return {
      fetcher: () =>
        apiClient.get<GetEventAndEditionFromEditionId>(
          '/edition/get-event-and-edition-from-edition-id',
          {
            params: { editionId }
          }
        ),
      key: `/edition/get-event-and-edition-from-edition-id?editionId=${editionId}`
    }
  }

  getJustificationDocuments = ({ editionId }: getRacesArgs) => ({
    fetcher: (() =>
      apiClient
        .get('/justification-documents', {
          params: { editionId }
        })
        .then((res) => res.data)) as Fetcher<
      UploadedDocumentWithLink[],
      string
    >,
    key: `${SWR_GET_CERTIFICATE_KEY}${editionId}`
  })

  getPreviewUrl = (key: string, uploadType: UploadType) =>
    apiClient.get<{ url: string | null }>('/get-preview-url', {
      params: { key, uploadType }
    })

  acceptMedicalCertificate = (key: string) =>
    apiClient.put<{ email: string | null }>('/validate-certificate', { key })

  getDecisionReasonsForDeclinedCertificate = () =>
    apiClient.get<DecisionReason[]>('/decision-reasons', {
      params: { type: DecisionReasonType.JUSTIFICATION_DOCUMENT_DECLINED }
    })

  getParticipantsExportFormats = () => ({
    fetcher: (() =>
      apiClient
        .get('/participants-export-formats')
        .then((res) => res.data)) as Fetcher<
      ParticipantsExportFormat[],
      string
    >,
    key: '/participants-export-formats'
  })

  denyMedicalCertificate = ({
    key,
    decisionCode,
    decisionComment,
    formKey
  }: {
    key: string
    decisionCode: string
    decisionComment?: string
    formKey?: string | null
  }) => {
    return apiClient.put<{ email: string | undefined }>('/deny-certificate', {
      key,
      decisionCode,
      decisionComment,
      formKey
    })
  }

  createFormForRaces = async ({
    editionId,
    title,
    description,
    type,
    races,
    defaultValue,
    choices,
    fields,
    validators,
    conditions,
    isBibAssignmentRule,
    content
  }: FieldValues & { editionId: number }) => {
    return await apiClient.post('/create-form-for-races', {
      editionId,
      title,
      description,
      type,
      races,
      defaultValue,
      choices,
      fields,
      validators,
      conditions,
      isBibAssignmentRule,
      content
    })
  }

  updateFormForRaces = async ({
    editionId,
    title,
    description,
    type,
    races,
    defaultValue,
    choices,
    fields,
    validators,
    key,
    conditions,
    isBibAssignmentRule,
    content
  }: FieldValues & { editionId: number }) => {
    return await apiClient.post('/update-form-for-races', {
      editionId,
      title,
      description,
      type,
      races,
      defaultValue,
      choices,
      fields,
      validators,
      key,
      isBibAssignmentRule,
      conditions,
      content
    })
  }

  createProductForRaces = async (
    values: FieldValues & { editionId: number }
  ) => {
    return await apiClient.post('/create-product-for-races', values)
  }

  updateProductForRaces = async (
    values: FieldValues & { editionId: number }
  ) => {
    return await apiClient.put('/update-product-for-races', values)
  }

  updateOptionsOrder = async (payload: {
    editionId: number
    elements: Record<string, number>
  }) => {
    return await apiClient.patch('/options/order', payload)
  }

  updateRacesOrder = async (payload: {
    editionId: EditionId
    races: Record<RaceId, number>
  }) => {
    return await apiClient.patch('/races/order', payload)
  }

  updateRaceIsActive = async ({
    raceId,
    isActive
  }: {
    raceId: number
    isActive: boolean
  }): Promise<void> => {
    await apiClient.post('/races/activate-or-disable-a-race', {
      raceId,
      isActive
    })
  }

  updateRaceIsArchived = async ({
    raceId,
    isArchived
  }: {
    raceId: number
    isArchived: boolean
  }): Promise<void> => {
    await apiClient.post('/races/update-is-archived', {
      raceId,
      isArchived
    })
  }

  updateRaceIsWaitingListEnabled = async ({
    raceId,
    isWaitingListEnabled
  }: {
    raceId: number
    isWaitingListEnabled: boolean
  }): Promise<void> => {
    await apiClient.post('/waiting-lists/update-is-waiting-list-enabled', {
      raceId,
      isWaitingListEnabled
    })
  }

  updateFormIsArchived = async (props: ArchiveFormSchemaType) => {
    await apiClient.post('/form/update-is-archived', props)
  }

  duplicateRace = async ({ raceId }: { raceId: number }): Promise<void> => {
    await apiClient.post('/races/duplicate-race', {
      raceId
    })
  }

  updateFormFunnelConfigsAreActive = async ({
    formKey,
    isActive
  }: {
    formKey: string
    isActive: boolean
  }): Promise<void> => {
    await apiClient.patch('/form-funnel-config/activate', {
      formKey,
      isActive
    })
  }

  updateDiscountIsDisabled = async ({
    editionId,
    discountId,
    isDisabled
  }: {
    editionId: number
    discountId: string
    isDisabled: boolean
  }): Promise<void> => {
    await apiClient.post('/discount/update-is-disabled', {
      editionId,
      discountId,
      isDisabled
    })
  }

  updateReductionIsDisabled = async ({
    editionId,
    reductionId,
    isDisabled
  }: {
    editionId: EditionId
    reductionId: ReductionId
    isDisabled: boolean
  }): Promise<void> => {
    await apiClient.post(
      `editions/${editionId}/reductions/${reductionId}/update-is-disabled`,
      {
        isDisabled
      }
    )
  }

  getGroups = ({ editionId }: GetGroupArgs) => {
    return {
      fetcher: () => apiClient.get<GroupList>(`/editions/${editionId}/groups`),
      key: editionId ? `/editions/${editionId}/groups` : null
    }
  }

  updateGroupIsActive = async ({
    editionId,
    groupId,
    isActive
  }: {
    editionId: EditionId
    groupId: GroupId
    isActive: boolean
  }): Promise<void> => {
    await apiClient.post(
      `editions/${editionId}/groups/${groupId}/update-is-active`,
      {
        isActive
      }
    )
  }

  getOrder(orderId: OrderId, editionId?: EditionId) {
    return {
      fetcher: () =>
        apiClient
          .get<Order>(`store/orders/${orderId}/get`, {
            params: { editionId }
          })
          .then((res) => res.data),
      key: editionId
        ? `/store/orders/${orderId}/get?editionId=${editionId}`
        : null
    }
  }

  getOrderAttendeeIds(orderId: OrderId) {
    return {
      fetcher: () =>
        apiClient
          .get<GetAttendeesFromOrder>(`store/orders/${orderId}/get-attendees`)
          .then((res) => res.data),
      key: `store/orders/${orderId}/get-attendees`
    }
  }

  getDraftOrderAttendeeIds(draftOrderId: string) {
    return {
      fetcher: () =>
        apiClient
          .get<GetAttendeesFromDraftOrder>(
            `store/draftOrders/${draftOrderId}/get-attendees`
          )
          .then((res) => res.data),
      key: `store/draftOrders/${draftOrderId}/get-attendees`
    }
  }

  markAsRefunded(orderId: OrderId, attendeeId: string, editionId?: EditionId) {
    return apiClient.post(`store/orders/${orderId}/mark-as-refunded`, {
      attendeeId,
      editionId
    })
  }

  getOrderEdits(orderId: OrderId | null, editionId?: EditionId) {
    return {
      key:
        orderId && editionId
          ? `/store/orders/${orderId}/get-order-edits?editionId=${editionId}`
          : null,
      fetcher: () =>
        apiClient
          .get<OrderEdit[]>(`store/orders/${orderId}/get-order-edits`, {
            params: { editionId }
          })
          .then((res) => res.data)
    }
  }

  updatePartnerOption(values: UpdatePartnerOptionArgs) {
    return apiClient.post(`partner-options/update`, values)
  }

  isDiscountCodeAlreadyUsed({ code }: { code: string | null }) {
    return {
      fetcher: () =>
        apiClient
          .get<{ status: DiscountAlreadyUsedStatus }>('/discount/alreadyUsed', {
            params: { code }
          })
          .then((res) => res.data),
      key: code ? `/discount/alreadyUsed?code=${code}` : null
    }
  }

  async bulkDeleteDiscounts(discountIds: string[], editionId: EditionId) {
    const res = await apiClient.delete<BulkDeleteDiscountsResult>(
      '/discounts/delete-bulk',
      { data: { discountIds, editionId } }
    )

    return res.data
  }

  async deleteDiscount(discountId: string, editionId: EditionId) {
    await apiClient.delete('/discount/' + discountId, { data: { editionId } })
  }

  async exportGeneric(
    apiPath: string,
    fileName: string,
    format: 'csv' | 'excel',
    locale: string,
    editionId?: EditionId,
    raceId?: RaceId
  ): Promise<void | ExportError> {
    try {
      const query = new URLSearchParams({
        format,
        ...(editionId ? { editionId: String(editionId) } : {}),
        ...(raceId ? { raceId: String(raceId) } : {}),
        locale
      }).toString()

      const { data, status } = await apiClient.get<Blob>(
        `${apiPath}?${query}`,
        { responseType: 'blob' }
      )

      if (status === 204) {
        return 'NO_DATA_FOUND'
      }

      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `${fileName}-${new Date().toISOString().split('T')[0]}.${
          format === 'excel' ? 'xlsx' : format
        }`
      )
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      if (error instanceof AxiosError && error.response) {
        if (error.response.status === 400) {
          return error.response.data.error as ExportError
        }
      }
      return 'UNKNOWN_ERROR'
    }
  }

  async exportDiscounts(
    format: 'csv' | 'excel',
    editionId: EditionId,
    locale: string
  ): Promise<void | EditionExportError> {
    return this.exportGeneric(
      'discounts/export',
      `discounts-export-edition-${editionId}-${format}`,
      format,
      locale,
      editionId
    )
  }

  async exportAwaitingRegistrations(
    format: 'csv' | 'excel',
    raceId: RaceId,
    locale: string,
    raceName: string
  ): Promise<void | RaceExportError> {
    return this.exportGeneric(
      'waiting-lists/export-registrations',
      `awaiting-registrations-${raceName}-${format}`,
      format,
      locale,
      undefined,
      raceId
    )
  }
}

const responseExport = z.object({
  exportFileUrl: z.string()
})

export const sendExportRequestService = async (params: ExportRequest) => {
  const response = await apiClient.post('attendees/export', params)
  return params.type === 'download' ? responseExport.parse(response.data) : null
}
